<template>
    <ds-modal
        :is-open="isOpen"
        class="share-feedback-modal"
        size="med"
        show-close
        :title="$options.MESSAGES.MODAL_TITLE"
        @close="$emit('close', false)"
    >
        <div class="form-header">
            <h3>{{ $options.MESSAGES.HEADER.LINE_1 }}</h3>

            <h3>{{ $options.MESSAGES.HEADER.LINE_2 }}</h3>
        </div>

        <form
            ref="form"
            data-qa="feedback-form"
            novalidate
            @submit.prevent="submitFeedback"
        >
            <label>{{ $options.MESSAGES.FEATURE.LABEL }}</label>

            <ds-select-field
                v-model="selectedFeature"
                bind-value-only
                :options="features"
                :label="$options.MESSAGES.FEATURE.PLACEHOLDER"
                required
                :submitted="submitted"
            />

            <label>{{ $options.MESSAGES.COMMENTS.LABEL }}</label>

            <ds-text-area-field
                v-model="feedback"
                :label="$options.MESSAGES.COMMENTS.PLACEHOLDER"
                required
                :submitted="submitted"
            />

            <label>{{ $options.MESSAGES.CONTACT.LABEL }}</label>

            <ds-radio-group
                v-model="contactConfirm"
                :options="contactConfirmOptions"
            />

            <div class="button-row">
                <ds-spinner v-if="submitting" class="submitting-spinner" data-qa="submitting-spinner" />
                <ds-filled-button v-else type="submit" data-qa="submit-button">
                    {{ $options.MESSAGES.SUBMIT_BUTTON }}
                </ds-filled-button>

                <ds-text-button data-qa="cancel-button" @click="$emit('close', false)">
                    {{ $options.MESSAGES.CANCEL }}
                </ds-text-button>
            </div>
        </form>
    </ds-modal>
</template>
<script>
import { FEATURES } from '@/components/shared/constants/shareFeedback.constants';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import axios from 'axios';

export const USER_CONTEXT_FEEDBACK_URL = '/app/shareFeedback';
export const MESSAGES = {
    MODAL_TITLE: 'Submit feedback',
    HEADER: {
        LINE_1: 'Our team thrives on feedback.',
        LINE_2: 'Please tell us about your experience.',
    },
    FEATURE: {
        LABEL: 'Select a feature to provide feedback about:',
        PLACEHOLDER: 'Select a feature',
    },
    COMMENTS: {
        LABEL: 'What would you like us to know?',
        PLACEHOLDER: 'Add your comments',
    },
    CONTACT: {
        LABEL: 'Can we contact you if we have follow-up questions?',
        YES: 'Yes, please contact me',
        NO: 'No thanks',
    },
    SUBMIT_BUTTON: 'Submit feedback',
    HOME_LABEL: 'Home (Dashboard)',
    LANDING_PAGES: 'Landing Pages',
    SUCCESS_MESSAGE: 'Thank you! Your feedback has been submitted.',
    FORMS_LABEL: 'Forms',
    NAVIGATION: 'Navigation',
    CANCEL: 'Cancel',
    YES: 'Yes',
    NO: 'No',
};

export default {
    MESSAGES,
    FEATURES,
    props: {
        isOpen: Boolean,
        feature: String,
        slackChannels: {
            type: Array,
            default: () => [SLACK_CHANNELS.IN_APP_FEEDBACK, SLACK_CHANNELS.MAX_CLASSIC_FEEDBACK],
        },
    },

    data() {
        return {
            features: FEATURES,
            selectedFeature: '',
            feedback: '',
            contactConfirm: MESSAGES.YES,
            submitted: false,
            submitting: false,
            contactConfirmOptions: [
                { value: MESSAGES.YES, label: MESSAGES.CONTACT.YES },
                { value: MESSAGES.NO, label: MESSAGES.CONTACT.NO },
            ],
        };
    },

    watch: {
        isOpen(value) {
            if (value) {
                this.reset();
            }
        },
    },

    methods: {
        async submitFeedback() {
            if (!this.$refs.form.checkValidity()) {
                return;
            }

            this.submitted = true;
            this.submitting = true;

            const response = await axios.get(USER_CONTEXT_FEEDBACK_URL);
            const feedbackContext = response.data;
            const text = [
                `User Name: ${feedbackContext.userName}`,
                `Feature: ${ this.selectedFeature }`,
                `Permission to Contact: ${this.contactConfirm}`,
                `Edition: ${feedbackContext.appEdition}`,
                `Feedback: ${this.feedback}`,
            ];

            window.fullName = feedbackContext.userName;
            window.username = feedbackContext.user;
            window.environment = feedbackContext.environment;

            const payload = {
                pretext: `App *${feedbackContext.appName}* left feedback.`,
                text: text.join('\n'),
                user: feedbackContext.user,
            };
            let googleDocInput = `${feedbackContext.googleDocFeedbackUrl}?`;

            googleDocInput = `${googleDocInput}${feedbackContext.googleDocUserEntry}=${feedbackContext.appName}`;
            googleDocInput = `${googleDocInput}&${feedbackContext.googleDocEmailEntry}=${feedbackContext.user}`;
            googleDocInput = `${googleDocInput}&${feedbackContext.googleDocUsernameEntry}=${feedbackContext.userName}`;
            googleDocInput = `${googleDocInput}&${feedbackContext.googleDocFeatureEntry}=${this.selectedFeature}`;
            googleDocInput = `${googleDocInput}&${feedbackContext.googleDocPermissionEntry}=${this.contactConfirm}`;
            googleDocInput = `${googleDocInput}&${feedbackContext.googleDocEditionEntry}=${feedbackContext.appEdition}`;
            googleDocInput = `${googleDocInput}&${feedbackContext.googleDocFeedbackEntry}=${this.feedback}&submit=Submit`;

            await Promise.all([
                this.postFeedbackInSlackChannels(payload),
                this.postFeedbackInGoogleDoc(googleDocInput),
            ]);

            this.$toast({ message: MESSAGES.SUCCESS_MESSAGE });
            this.$emit('close', true);
        },

        async postFeedbackInSlackChannels(payload) {
            await Promise.allSettled(this.slackChannels.map(async (slackChannel) => {
                await this.$slack(slackChannel, payload);
            }));
        },

        async postFeedbackInGoogleDoc(payload) {
            await fetch(payload, {
                method: 'GET',
                mode: 'no-cors',
                cache: 'no-cache',
                credentials: 'omit',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
        },

        reset() {
            this.submitted = false;
            this.submitting = false;
            this.feedback = '';
            this.contactConfirm = MESSAGES.YES;
            this.selectedFeature = this.features?.find((e) => e.value === this.feature) ? this.feature : '';
        },
    },
};
</script>
<style lang="scss" scoped>
.share-feedback-modal {
    --radio-padding: 0 0 #{$spacing-200} #{$spacing-200};
}

.form-header {
    text-align: center;
    margin-bottom: $spacing-300;
}

form {
    width: 100%;
    max-width: 31.25rem;
    margin: 0 auto;

    label {
        margin-bottom: $spacing-200;
    }
}

.button-row {
    margin-top: $spacing-200;
}

.submitting-spinner {
    margin-right: $spacing-800;
}
</style>
